import type { BadgeTone } from '@fas/ui';
import {
  Permissions as P,
  type ApprovalRequestStatus,
  type ApprovalStepState,
  type CalendarEventType,
  type CompensationCategory,
  type CorrectionReason,
  type LeaveType,
  type MakeupStatus,
  type OverloadAssignmentType,
  type PermissionCode,
  type ProofStatus,
  type ProofType,
  type RequestStatus,
  type SessionCreditPolicy,
  type SubstituteCompensationPolicy,
  type WorkflowCode,
} from '@fas/types';

type Label = { label: string; tone: BadgeTone };

/** §76 rule 20: pending, approved and final states are always distinguishable by text. */
export const REQUEST_STATUS: Record<RequestStatus, Label> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  SUBMITTED: { label: 'Submitted', tone: 'warning' },
  UNDER_REVIEW: { label: 'Under review', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Not approved', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'success' },
};

export const MAKEUP_STATUS: Record<MakeupStatus, Label> = {
  DRAFT: { label: 'Draft', tone: 'neutral' },
  SUBMITTED: { label: 'Submitted', tone: 'warning' },
  CHAIR_REVIEW: { label: 'Chair review', tone: 'warning' },
  DEAN_REVIEW_OPTIONAL: { label: 'Dean review', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Not approved', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
  COMPLETED: { label: 'Completed', tone: 'success' },
};

export const APPROVAL_STATUS: Record<ApprovalRequestStatus, Label> = {
  PENDING: { label: 'Pending', tone: 'warning' },
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Not approved', tone: 'danger' },
  CANCELLED: { label: 'Cancelled', tone: 'neutral' },
};

export const STEP_STATE: Record<ApprovalStepState, Label> = {
  APPROVED: { label: 'Approved', tone: 'success' },
  REJECTED: { label: 'Not approved', tone: 'danger' },
  PENDING: { label: 'Waiting for decision', tone: 'warning' },
  WAITING: { label: 'Not yet reached', tone: 'neutral' },
  CANCELLED: { label: 'Not reached', tone: 'neutral' },
};

export const WORKFLOW_LABEL: Record<WorkflowCode, string> = {
  ATTENDANCE_CORRECTION: 'Attendance correction',
  MAKEUP: 'Makeup class',
  SUBSTITUTION: 'Substitution',
  OVERLOAD: 'Overload / extra load',
  DUTY: 'Administrative duty',
  LEAVE: 'Leave',
};

export const CORRECTION_REASON: Record<CorrectionReason, string> = {
  FORGOT_CLOCK_IN: 'Forgot to clock in',
  FORGOT_CLOCK_OUT: 'Forgot to clock out',
  GPS_FAILURE: 'GPS failure',
  INTERNET_FAILURE: 'Internet failure',
  SYSTEM_OUTAGE: 'System outage',
  INCORRECT_SCHEDULE: 'Incorrect schedule',
  OFFICIAL_ASSIGNMENT: 'Official assignment',
  EMERGENCY: 'Emergency',
  WRONG_MODALITY: 'Wrong modality recorded',
  OTHER: 'Other',
};

export const PROOF_TYPE: Record<ProofType, string> = {
  ONLINE_CLASS_SCREENSHOT: 'Online class screenshot',
  MEETING_ATTENDANCE_LOG: 'Meeting attendance log',
  LMS_ACTIVITY: 'LMS activity',
  SESSION_LOG: 'Session log',
  SYLLABUS_PROGRESS: 'Syllabus progress',
  LESSON_PLAN: 'Lesson plan',
  CLASSROOM_PHOTO: 'Classroom photo',
  STUDENT_ATTENDANCE: 'Student attendance sheet',
  OTHER: 'Other',
};

export const PROOF_STATUS: Record<ProofStatus, Label> = {
  PENDING_REVIEW: { label: 'Pending review', tone: 'warning' },
  ACCEPTED: { label: 'Accepted', tone: 'success' },
  REJECTED: { label: 'Rejected', tone: 'danger' },
};

export const LEAVE_TYPE: Record<LeaveType, string> = {
  VACATION: 'Vacation leave',
  SICK: 'Sick leave',
  EMERGENCY: 'Emergency leave',
  MATERNITY: 'Maternity leave',
  PATERNITY: 'Paternity leave',
  BEREAVEMENT: 'Bereavement leave',
  STUDY: 'Study leave',
  OFFICIAL_BUSINESS: 'Official business',
  OTHER: 'Other leave',
};

export const CALENDAR_EVENT_TYPE: Record<CalendarEventType, string> = {
  NATIONAL_HOLIDAY: 'National holiday',
  LOCAL_HOLIDAY: 'Local holiday',
  SCHOOL_HOLIDAY: 'School holiday',
  CLASS_SUSPENSION: 'Class suspension',
  WORK_SUSPENSION: 'Work suspension',
  FACULTY_DEVELOPMENT: 'Faculty development',
  INSTITUTIONAL_ACTIVITY: 'Institutional activity',
  OTHER: 'Other event',
};

export const SUBSTITUTE_COMPENSATION: Record<SubstituteCompensationPolicy, string> = {
  SUBSTITUTE_PAYABLE: 'Substitute is credited',
  ORIGINAL_PAYABLE: 'Original faculty is credited',
  NO_ADDITIONAL_PAY: 'No additional pay',
  EXTRA_LOAD: 'Counted as extra load',
  MANUAL_REVIEW: 'Decide during review',
};

export const COMPENSATION_CATEGORY: Record<CompensationCategory, string> = {
  OVERLOAD_PAY: 'Overload pay',
  EXTRA_LOAD_PAY: 'Extra-load pay',
  HONORARIUM: 'Honorarium',
  SERVICE_CREDIT: 'Service credit',
  NO_COMPENSATION: 'No compensation',
};

export const OVERLOAD_ASSIGNMENT_TYPE: Record<OverloadAssignmentType, string> = {
  OVERLOAD_TEACHING: 'Overload teaching',
  ADDITIONAL_LECTURE: 'Additional lecture',
  LABORATORY_SUPERVISION: 'Laboratory supervision',
  CLINICAL_SUPERVISION: 'Clinical supervision',
  SEMINAR: 'Seminar',
  SPECIAL_CONSULTATION: 'Special consultation',
  INSTITUTIONAL_DUTY: 'Institutional duty',
  OTHER: 'Other',
};

export const CREDIT_POLICY: Record<SessionCreditPolicy, Label> = {
  STANDARD: { label: 'Creditable', tone: 'success' },
  NON_PAYABLE: { label: 'Not payable', tone: 'neutral' },
  MANUAL_REVIEW: { label: 'Credit decided on review', tone: 'warning' },
};

export type RequestKind = 'corrections' | 'makeup' | 'substitutions' | 'overloads' | 'duties' | 'leave';

export interface RequestKindConfig {
  kind: RequestKind;
  /** Tab / page title. */
  label: string;
  singular: string;
  /** API collection path. */
  api: string;
  /** Attachment entity type of the request records. */
  entityType: string;
  workflow: WorkflowCode;
  /** Permissions that may file a request of this kind from the requests page. */
  file: PermissionCode[];
  /** Permissions that may list requests of this kind (own or in scope). */
  read: PermissionCode[];
  /** Report path segment (`/reports/<report>`), when the kind has a report. */
  report: string | null;
}

export const REQUEST_KINDS: Record<RequestKind, RequestKindConfig> = {
  corrections: {
    kind: 'corrections',
    label: 'Corrections',
    singular: 'Attendance correction',
    api: '/corrections',
    entityType: 'attendance_correction',
    workflow: 'ATTENDANCE_CORRECTION',
    // Corrections are filed from a specific attendance record, not from the list.
    file: [],
    read: [P.ATTENDANCE_CORRECT_REQUEST, P.ATTENDANCE_CORRECT_APPROVE, P.ATTENDANCE_READ_DEPARTMENT, P.ATTENDANCE_VERIFY, P.ATTENDANCE_APPROVE, P.ATTENDANCE_FINALIZE],
    report: 'corrections',
  },
  makeup: {
    kind: 'makeup',
    label: 'Makeup classes',
    singular: 'Makeup class',
    api: '/makeup-requests',
    entityType: 'makeup_request',
    workflow: 'MAKEUP',
    file: [P.MAKEUP_REQUEST],
    read: [P.MAKEUP_REQUEST, P.MAKEUP_APPROVE, P.SCHEDULE_READ_DEPARTMENT, P.SCHEDULE_MANAGE, P.ATTENDANCE_READ_DEPARTMENT],
    report: 'makeup',
  },
  substitutions: {
    kind: 'substitutions',
    label: 'Substitutions',
    singular: 'Substitution',
    api: '/substitutions',
    entityType: 'substitute_assignment',
    workflow: 'SUBSTITUTION',
    file: [P.SUBSTITUTION_REQUEST, P.SUBSTITUTION_APPROVE],
    read: [P.SUBSTITUTION_REQUEST, P.SCHEDULE_READ_SELF, P.SUBSTITUTION_APPROVE, P.SCHEDULE_READ_DEPARTMENT, P.SCHEDULE_MANAGE, P.ATTENDANCE_READ_DEPARTMENT],
    report: 'substitutions',
  },
  overloads: {
    kind: 'overloads',
    label: 'Overload / extra load',
    singular: 'Overload / extra-load assignment',
    api: '/overloads',
    entityType: 'overload_assignment',
    workflow: 'OVERLOAD',
    file: [P.OVERLOAD_REQUEST],
    read: [P.OVERLOAD_REQUEST, P.ATTENDANCE_READ_SELF, P.OVERLOAD_APPROVE, P.FACULTY_LOAD_MANAGE, P.ATTENDANCE_READ_DEPARTMENT, P.REPORT_READ_DEPARTMENT],
    report: 'overload',
  },
  duties: {
    kind: 'duties',
    label: 'Duties',
    singular: 'Administrative duty',
    api: '/duties',
    entityType: 'administrative_duty',
    workflow: 'DUTY',
    file: [P.DUTY_REQUEST, P.DUTY_MANAGE],
    read: [P.DUTY_REQUEST, P.ATTENDANCE_READ_SELF, P.DUTY_APPROVE, P.DUTY_MANAGE, P.ATTENDANCE_READ_DEPARTMENT, P.REPORT_READ_DEPARTMENT],
    report: 'duties',
  },
  leave: {
    kind: 'leave',
    label: 'Leave',
    singular: 'Leave',
    api: '/leave-requests',
    entityType: 'leave_request',
    workflow: 'LEAVE',
    file: [P.LEAVE_REQUEST, P.LEAVE_MANAGE],
    read: [P.LEAVE_REQUEST, P.LEAVE_APPROVE, P.LEAVE_MANAGE],
    report: null,
  },
};

export const REQUEST_KIND_ORDER: RequestKind[] = ['corrections', 'makeup', 'substitutions', 'overloads', 'duties', 'leave'];

export function isRequestKind(value: string | undefined): value is RequestKind {
  return !!value && (REQUEST_KIND_ORDER as string[]).includes(value);
}

/** Any permission that shows the "Requests" area. */
export const REQUEST_AREA_PERMISSIONS: PermissionCode[] = [...new Set(REQUEST_KIND_ORDER.flatMap((k) => REQUEST_KINDS[k].read))];

/** Byte count â†’ "1.2 MB". */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
